import {formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {AdbChartTheme, adbChartTheme, adbRedrawOnThemeChange} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdbUsageTrendLayer,
  adbUsageTrendEmpty,
  adbUsageTrendNotes,
  adbUsageTrendOption,
  adbUsageWeekTitle
} from '@features/admin-panel/helpers/admin-dashboard-usage-chart';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardUsageTrend} from '@features/admin-panel/models/admin-dashboard-usage.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const scenariosTrendCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The tooltip's last line (Plan ITP-P-50 Q9): the type split counts a multi-type scenario in every type it has. */
const MULTI_TYPE_NOTE = 'A scenario with more than one type counts once in each.';

/**
 * A scenario type's colour on the stacked bars (ITP-E-214-03): fixed per type, so a type keeps its colour whatever else
 * is present. GMT blue, CbC purple, ITC green, TXP orange, PCbC amber, Custom red; Other and any unknown type ink 3.
 */
export function scenarioTypeColor(t: AdbChartTheme, type: string): string {
  switch (type) {
    case 'GMT':
      return t.blue;
    case 'CbC':
      return t.purple;
    case 'ITC':
      return t.green;
    case 'TXP':
      return t.orange;
    case 'PCbC':
      return t.amber;
    case 'Custom':
      return t.red;
    default:
      return t.ink3;
  }
}

/**
 * "Projects, scenarios & entities" → "Scenarios" (ITP-E-214-03, Plan ITP-P-50 story 9): scenarios created per week over
 * 26 weeks stacked by type, the current week partial, the 12-week forecast of the total with its 80 % band, the totals,
 * and the notes of story 12. Reloads whenever the dashboard's query params change (the query ignores the period).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-scenarios-trend',
  templateUrl: './admin-dashboard-scenarios-trend.component.html',
  styleUrls: ['./admin-dashboard-scenarios-trend.component.scss']
})
export class AdminDashboardScenariosTrendComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly cardTitle = 'Scenarios';
  readonly description = 'Scenarios created per week by type, with a 12-week forecast of the total.';
  readonly emptyTitle = 'No scenarios in this view';
  readonly failedTitle = 'Couldn\'t load scenarios';

  data: AdminDashboardUsageTrend | null = null;
  /** The notes under the chart (story 12), set with each response. */
  notes: string[] = [];
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  /** The chart container exists only while there is something to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = scenariosTrendCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** No response, or no scenario created in the 26 weeks while the total is unknown or 0. */
  get isEmpty(): boolean {
    return adbUsageTrendEmpty(this.data);
  }

  /** "1,500 scenarios · 300 worked on in the last 30 days"; only the total while the active count is unknown; '' without a total. */
  get numbersText(): string {
    const total = this.data?.total;
    if (total === null || total === undefined) {
      return '';
    }
    const active = this.data.active;
    return active === null || active === undefined
      ? countOf(total, 'scenario')
      : `${countOf(total, 'scenario')} · ${formatNumber(active, 'en-US', '1.0-0')} worked on in the last 30 days`;
  }

  /**
   * "Week of 28 Sep: 1,000 scenarios created<br/>GMT: 700<br/>CbC: 400<br/>" then the muted multi-type note; a type
   * with nothing that week is left out; "Week of 5 Oct, so far: …" for the current week.
   */
  tooltipText(index: number, ink2: string): string {
    const week = this.data?.weeks?.[index];
    if (!week) {
      return '';
    }
    const lines = [`${adbUsageWeekTitle(week)}: ${countOf(week.value, 'scenario')} created`];
    for (const type of this.data.types ?? []) {
      const value = week.byType?.[type] ?? 0;
      if (value > 0) {
        lines.push(`${type}: ${formatNumber(value, 'en-US', '1.0-0')}`);
      }
    }
    return `${lines.join('<br/>')}<br/><span style="color:${ink2}">${MULTI_TYPE_NOTE}</span>`;
  }

  /** The card for the PDF report, built from what it shows now; never reloads. */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: this.cardTitle, text: this.failedTitle}];
    }
    if (this.isEmpty) {
      return [{kind: 'empty', title: this.cardTitle, text: this.emptyTitle}];
    }
    const block: AdminDashboardReportBlock = {
      kind: 'chart',
      title: this.cardTitle,
      subtitle: this.numbersText || this.description,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    };
    if (this.notes.length) {
      block.note = this.notes.join(' · ');
    }
    return [block];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardUsageTrend>(
      'AdminDashboardScenariosTrendQuery', this.params, 'Trend'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardUsageTrend | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.notes = adbUsageTrendNotes(data);
    this.render();
    this.report(failed ? 'failed' : this.isEmpty ? 'empty' : 'content');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.data) {
      return;
    }
    // Colours come from the card's --adb-* tokens: one bar layer per type (scenarioTypeColor), the forecast in ink 2.
    const t = adbChartTheme(this.host.nativeElement);
    const weeks = this.data.weeks ?? [];
    const types = this.data.types ?? [];
    const layers: AdbUsageTrendLayer[] = types.length
      ? types.map(type => ({name: type, color: scenarioTypeColor(t, type), values: weeks.map(week => week.byType?.[type] ?? 0)}))
      : [{name: 'Scenarios created', color: t.blue, values: weeks.map(week => week.value)}];
    this.chart.setOption(adbUsageTrendOption(t, {
      weeks,
      forecast: this.data.forecast,
      kind: 'bar',
      layers,
      weekTooltip: index => this.tooltipText(index, t.ink2)
    }), true);
  }
}
